import { zodResolver } from '@hookform/resolvers/zod';
import {
  type IUpdateBatchData,
  type IUpdateBatchVar,
  UPDATE_BATCH,
} from '@quesmed/types/resolvers/mutation/admin';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ErrorFeedback, Heading, SuccessFeedback } from '@/core/molecules';
import { Alert, AnimatedComponent, Button, cn, FormInput } from '@/core/ui';
import { useMutation } from '@/libs';

const schema = z.object({
  batchId: z.uuid({ message: 'Invalid Batch Id' }).min(1, 'BatchId is required'),
});

type ISchema = z.infer<typeof schema>;

const BatchOverwrite = () => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<ISchema>({
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const [updateBatch, { loading, data }] = useMutation<IUpdateBatchData, IUpdateBatchVar>(UPDATE_BATCH);

  const finalData = useMemo(() => data?.admin?.updateBatch, [data?.admin?.updateBatch]);

  const onSubmit: SubmitHandler<ISchema> = async (input) => {
    await updateBatch({ variables: input });
  };

  return (
    <div className='flex flex-col space-y-2 justify-center w-full items-center'>
      <div className='bg-background rounded-lg shadow-md w-full max-w-5xl z-[2]'>
        <Heading title='OverWrite Batch Data' />
        <form onSubmit={handleSubmit(onSubmit)} className='p-6 md:p-10 '>
          <Alert
            message='Please read below before proceeding!'
            type='warn'
            className='mb-10'
            list={[
              'Please Make sure correct DB storage is selected!',
              'If data is still not visible, It maybe because data is cached, wait for a day or ask dev to clear cache',
            ]}
          />
          <div className='grid grid-cols-2 items-start gap-y-6 md:gap-y-10 gap-x-8'>
            <FormInput
              isRequired={true}
              description='Enter the batchId from validation step'
              control={control}
              name='batchId'
              labelPlacement='inside'
              placeholder='04fe9191-24da-47bc-80d5-61f5089337ab'
              className='rounded col-span-2'
              variant='faded'
              label='Batch Id'
            />
          </div>
          <div className='flex justify-center mt-10'>
            <Button
              isDisabled={!isValid}
              isLoading={loading}
              className='w-full max-w-xl bg-primary'
              type='submit'
              radius='sm'
              size='lg'
            >
              Submit
            </Button>
          </div>
        </form>
      </div>

      <AnimatedComponent>
        {finalData && (
          <motion.div
            initial={{ y: -160 }}
            animate={{ y: 0 }}
            exit={{ y: -160 }}
            transition={{ type: 'spring' }}
            className={cn(
              'flex flex-col space-y-4 justify-center items-center',
              'w-full max-w-5xl py-5',
              'bg-background rounded-lg shadow-md ',
            )}
          >
            {finalData?.status === 200 ? (
              <SuccessFeedback message='Overwrite Batch completed!' response={finalData} />
            ) : (
              <ErrorFeedback response={finalData} />
            )}
          </motion.div>
        )}
      </AnimatedComponent>
    </div>
  );
};

export default BatchOverwrite;
